import { useCallback, useRef } from 'react';

const usePasteUpload = (onFilePaste, acceptTypes = null, maxSize = 5 * 1024 * 1024) => {
  const pasteRef = useRef(null);

  const handlePaste = useCallback(async (event) => {
    event.preventDefault();
    
    const items = event.clipboardData?.items;
    if (!items) return;

    const files = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          // Check file size
          if (file.size > maxSize) {
            console.warn(`File ${file.name} is too large. Max size: ${maxSize / (1024 * 1024)}MB`);
            continue;
          }
          
          // Check file type if acceptTypes is specified
          if (acceptTypes && !acceptTypes.some(type => file.type === type)) {
            console.warn(`File type ${file.type} not allowed`);
            continue;
          }
          
          files.push(file);
        }
      } else if (item.kind === 'string' && item.type === 'text/html') {
        // Handle HTML content (e.g., copied from web pages)
        item.getAsString((htmlString) => {
          // Convert HTML to file if it contains image data
          const imgMatch = htmlString.match(/<img[^>]+src="data:([^"]+)"/);
          if (imgMatch) {
            const dataUrl = imgMatch[0].match(/src="([^"]+)"/)[1];
            const base64Data = dataUrl.split(',')[1];
            const mimeType = dataUrl.split(':')[1].split(';')[0];
            
            if (acceptTypes && !acceptTypes.includes(mimeType)) {
              console.warn(`Image type ${mimeType} not allowed`);
              return;
            }
            
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let j = 0; j < byteCharacters.length; j++) {
              byteNumbers[j] = byteCharacters.charCodeAt(j);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const file = new File([byteArray], `pasted-image-${Date.now()}.png`, { type: mimeType });
            
            if (file.size <= maxSize) {
              files.push(file);
            }
          }
        });
      }
    }
    
    if (files.length > 0) {
      onFilePaste(files);
    }
  }, [onFilePaste, acceptTypes, maxSize]);

  const setupPasteListener = useCallback((element) => {
    if (element) {
      pasteRef.current = element;
      element.addEventListener('paste', handlePaste);
      element.addEventListener('dragover', (e) => e.preventDefault());
      element.addEventListener('drop', (e) => e.preventDefault());
      
      // Add visual feedback
      element.addEventListener('dragenter', () => {
        element.classList.add('paste-active');
      });
      
      element.addEventListener('dragleave', () => {
        element.classList.remove('paste-active');
      });
      
      return () => {
        element.removeEventListener('paste', handlePaste);
        element.removeEventListener('dragover', (e) => e.preventDefault());
        element.removeEventListener('drop', (e) => e.preventDefault());
        element.removeEventListener('dragenter', () => {
          element.classList.add('paste-active');
        });
        element.removeEventListener('dragleave', () => {
          element.classList.remove('paste-active');
        });
      };
    }
  }, [handlePaste]);

  return { setupPasteListener };
};

export default usePasteUpload; 